import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scale, ShieldCheck, Compass, BookOpen, MessageSquare, ArrowRight,
  Gavel, FileSearch, Handshake, Users, Milestone, MapPin, Sparkles, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATES, ROADMAP_STAGES, RIGHTS } from "@/data/legalData";
import { GUIDES } from "@/data/guides";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const stageIcons = { arrest: ShieldCheck, arraignment: Gavel, discovery: FileSearch, preliminary: Scale, plea: Handshake, trial: Users, sentencing: Milestone };

const features = [
  { icon: Compass, title: "7-Stage Case Roadmap", body: "See exactly what happens from arrest to sentencing — and what your defense should be doing at each step.", to: "/roadmap", tag: "Roadmap" },
  { icon: MessageSquare, title: "AI Legal Companion", body: "Ask anything about your charges, court dates, or options and get calm, plain-English answers any time.", to: "/assistant", tag: "AI Chat" },
  { icon: BookOpen, title: "Resource Library", body: "Know your rights, a 100-term plain-English glossary, and a 50-state + federal procedure explorer.", to: "/resources", tag: "Library" },
  { icon: Lock, title: "Confidential Case Vault", body: "Privately track charges, court dates, questions for your lawyer, and release conditions in one place.", to: "/dashboard", tag: "Vault" },
];

export default function Landing() {
  const [state, setState] = useState("");

  return (
    <div className="grain-bg">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-forest-600/20 bg-forest-100 px-3 py-1.5">
              <Sparkles className="h-3.5 w-3.5 text-forest-700" />
              <span className="font-mono text-xs uppercase tracking-[0.16em] text-forest-800">US Federal & All 50 States</span>
            </div>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-forest-900">
              Understand your criminal case, and take the next step with confidence.
            </h1>
            <p className="mt-6 text-lg text-ink-700 leading-relaxed max-w-xl">
              A criminal charge is frightening and confusing. SentinelGuide turns the process into clear,
              calm, step-by-step guidance — so you know your rights, what's coming, and how to move forward.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/assistant" data-testid="hero-assistant-cta">
                <Button className="rounded-full h-12 px-6 bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2 w-full sm:w-auto">
                  Talk to the AI Companion <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/roadmap" data-testid="hero-roadmap-cta">
                <Button variant="outline" className="rounded-full h-12 px-6 border-forest-800/20 bg-white text-forest-800 hover:bg-parchment-100 w-full sm:w-auto">
                  Explore the case roadmap
                </Button>
              </Link>
            </div>

            <div className="mt-8 flex items-center gap-3 rounded-2xl border border-parchment-200 bg-white p-3 max-w-md">
              <MapPin className="h-5 w-5 text-terracotta shrink-0 ml-1" />
              <Select value={state} onValueChange={setState}>
                <SelectTrigger data-testid="hero-state-select" className="border-0 shadow-none focus:ring-0 text-ink-700">
                  <SelectValue placeholder="Select your jurisdiction to personalize" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {STATES.map((s) => (
                    <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {state && (
                <Link to="/resources" state={{ state }}>
                  <Button size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white shrink-0">Go</Button>
                </Link>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="relative rounded-3xl overflow-hidden border border-parchment-200 shadow-[0_20px_60px_rgba(27,59,50,0.15)]">
              <img src="https://images.unsplash.com/photo-1636652966850-5ac4d02370e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Neoclassical courthouse" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-900/80 via-forest-900/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="rounded-2xl bg-parchment-50/95 backdrop-blur p-4 border border-white/40">
                  <div className="flex items-center gap-2 text-forest-700">
                    <ShieldCheck className="h-4 w-4" />
                    <span className="font-mono text-[11px] uppercase tracking-wider">Presumed innocent</span>
                  </div>
                  <p className="mt-1 text-sm text-ink-700">The prosecution must prove guilt <strong>beyond a reasonable doubt</strong>. You have rights at every stage.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}>
              <Link to={f.to} data-testid={`feature-card-${f.to.slice(1) || "vault"}`}
                className="group block h-full rounded-2xl border border-parchment-200 bg-white p-7 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(27,59,50,0.1)] transition-all duration-200">
                <div className="flex items-start justify-between">
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-forest-100 text-forest-800 group-hover:bg-forest-800 group-hover:text-parchment-50 transition-colors">
                    <f.icon className="h-6 w-6" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">{f.tag}</span>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-forest-900">{f.title}</h3>
                <p className="mt-2 text-ink-700 leading-relaxed">{f.body}</p>
                <div className="mt-4 flex items-center gap-1.5 text-terracotta text-sm font-medium">
                  Open <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ROADMAP PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">The journey ahead</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-forest-900">The 7 stages of a criminal case</h2>
          <p className="mt-3 text-ink-700">From the moment of arrest to sentencing and appeals — here's the path, demystified.</p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROADMAP_STAGES.map((s, i) => {
            const Icon = stageIcons[s.id] || Milestone;
            return (
              <motion.div key={s.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-parchment-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-forest-600">STAGE {s.n}</span>
                  <Icon className="h-5 w-5 text-forest-700" />
                </div>
                <h3 className="mt-3 font-semibold text-forest-900 leading-snug">{s.title}</h3>
                <p className="mt-1.5 text-sm text-ink-500 leading-relaxed line-clamp-3">{s.summary}</p>
              </motion.div>
            );
          })}
          <Link to="/roadmap" className="rounded-2xl border-2 border-dashed border-forest-600/30 bg-forest-50 p-5 flex flex-col justify-center items-start hover:bg-forest-100 transition-colors">
            <Milestone className="h-6 w-6 text-forest-700" />
            <h3 className="mt-3 font-semibold text-forest-900">See full roadmap</h3>
            <p className="mt-1 text-sm text-forest-700 flex items-center gap-1">Deep dive each stage <ArrowRight className="h-3.5 w-3.5" /></p>
          </Link>
        </div>
      </section>

      {/* RIGHTS STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="rounded-3xl bg-forest-900 text-parchment-50 p-8 sm:p-12 overflow-hidden relative">
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Know your rights</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold">Rights that protect you at every stage</h2>
              <p className="mt-4 text-parchment-200/80">Whether you're stopped, questioned, or in court, these constitutional protections are always yours.</p>
              <Link to="/resources" state={{ tab: "rights" }}>
                <Button className="mt-6 rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">All rights guides <ArrowRight className="h-4 w-4" /></Button>
              </Link>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {RIGHTS.slice(0, 4).map((r) => (
                <div key={r.title} className="rounded-2xl bg-forest-800/60 border border-forest-600/30 p-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <ShieldCheck className="h-5 w-5 text-sage-500" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-parchment-200/70">{r.statute}</span>
                  </div>
                  <h3 className="font-semibold">{r.title}</h3>
                  <p className="mt-1 text-sm text-parchment-200/70 line-clamp-2">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="landing-state-guides" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Guides by state</span>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-forest-900">How criminal cases work where you live</h2>
        <div className="mt-8 flex flex-wrap gap-2">
          {GUIDES.map((g) => (
            <Link key={g.slug} to={`/guides/${g.slug}`} className="text-sm px-3.5 py-1.5 rounded-full border border-parchment-200 bg-white text-ink-700 hover:border-forest-800 hover:text-forest-900 transition-colors">
              {g.federal ? "Federal" : g.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
